import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ManualPaymentService, ManualPaymentRequestResponse } from "@/api/manualPaymentService";
import { formatPrice } from "@/util/formatPrice";
import LoadingSection from "@/components/LoadingSection";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/Alert";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useUsersOrders } from "@/queries/order/useUsersOrders";
import { useProfileContext } from "@/contexts/ProfileContext";
import ManualBalanceCheckout from "./ManualBalanceCheckout";

export default function ManualPayment() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const requestId = searchParams.get("requestId");
    const source = searchParams.get("source");
    const isTrackerOrigin = source === "tracker";
    const { authentication, isAuthenticationLoading } = useProfileContext();

    const [loading, setLoading] = useState(true);
    const [request, setRequest] = useState<ManualPaymentRequestResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const { data: userOrders, isLoading: isUserOrdersLoading, isError: isUserOrdersError } = useUsersOrders({
        queryConfig: {
                        enabled:
                                !!requestId &&
                                !loading &&
                                !error &&
                                !isAuthenticationLoading &&
                                !!authentication?.loggedIn,
        },
    });

    useEffect(() => {
        if (!token && !requestId) {
            setError("Invalid payment link. Missing token or request ID.");
            setLoading(false);
            return;
        }

        if (token) {
            ManualPaymentService.resolveToken(token)
                .then((res) => {
                    setRequest(res);
                    setLoading(false);
                })
                .catch((err) => {
                    console.error("Failed to resolve payment token", err);
                    setError(err.body?.message || "Failed to load payment details. The link may be expired or already used.");
                    setLoading(false);
                });
        } else if (requestId) {
            if (isTrackerOrigin && isAuthenticationLoading) {
                return;
            }

            if (isTrackerOrigin && !authentication?.loggedIn) {
                setError("Please sign in again to view this payment request.");
                setLoading(false);
                return;
            }

            ManualPaymentService.getRequestByUuid(requestId)
                .then((res) => {
                    setRequest(res);
                    setLoading(false);
                })
                .catch((err) => {
                    console.error("Failed to load payment request", err);
                    if (err?.status === 401 || err?.status === 403) {
                        setError("Please sign in again to view this payment request.");
                        return;
                    }
                    setError(err.body?.message || "Failed to load payment details. The request may not exist.");
                    setLoading(false);
                });
        }
    }, [token, requestId, isTrackerOrigin, isAuthenticationLoading, authentication?.loggedIn]);


    if (loading) {
        return <LoadingSection />;
    }

    if (error) {
        return (
            <div className="max-w-lg mx-auto p-6">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            </div>
        );
    }

    if (!request) return null;

    const isPaid = request.state === "Paid";

    const matchingOrder = request.order || userOrders?.find((order) => order.uuid === request.orderUuid);

    if (isUserOrdersError && !request.order) {
        return (
            <div className="max-w-lg mx-auto p-6">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>
                        We could not load your order details for the payment options screen.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    if (!matchingOrder && (isAuthenticationLoading || (isTrackerOrigin && isUserOrdersLoading))) {
        return <LoadingSection />;
    }

    if (matchingOrder) {
        return <ManualBalanceCheckout request={request} order={matchingOrder} token={token} />;
    }

    return (
        <div className="max-w-lg mx-auto p-6">
            <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Order Not Found</AlertTitle>
                <AlertDescription>
                    We could not find the order associated with this payment request. 
                    If you are using a direct link, please try signing in to your account.
                </AlertDescription>
            </Alert>
            <div className="mt-6 flex justify-center">
                <Button asChild>
                    <Link to="/login">Sign In</Link>
                </Button>
            </div>
        </div>
    );
}
